import React from 'react';
import visionLiveLogo from '../assets/images/visionlive.png';
import mojenaocaleLogo from '../assets/branding/mojenaocale-logo.webp';

/**
 * Main site footer used on landing and blog pages.
 * Props: onNavigateToTerms, onNavigateToPrivacy, onNavigateToCookiePolicy, onNavigateToApiDocs, onNavigateToBlog
 */
const SiteFooter = ({
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookiePolicy,
  onNavigateToApiDocs,
  onNavigateToBlog,
}) => {
  const handleTermsClick = (e) => {
    if (onNavigateToTerms) {
      e.preventDefault();
      onNavigateToTerms();
    }
  };
  const handlePrivacyClick = (e) => {
    if (onNavigateToPrivacy) {
      e.preventDefault();
      onNavigateToPrivacy();
    }
  };
  const handleCookiePolicyClick = (e) => {
    if (onNavigateToCookiePolicy) {
      e.preventDefault();
      onNavigateToCookiePolicy();
    }
  };
  const handleApiDocsClick = (e) => {
    if (onNavigateToApiDocs) {
      e.preventDefault();
      onNavigateToApiDocs();
    }
  };
  const handleBlogClick = (e) => {
    if (onNavigateToBlog) {
      e.preventDefault();
      onNavigateToBlog();
    }
  };

  return (
    <footer className="bg-[#0d0d0d] border-t border-white/10 py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img
                src={visionLiveLogo}
                alt="VisionLive"
                className="h-8 w-auto max-w-[180px] object-contain"
              />
            </div>
            <p className="text-white/50 text-sm mb-4 max-w-xs">
              The world's most converting virtual try-on solution for e-commerce businesses.
            </p>
            <a
              href="https://mojenaocale.ba"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm text-white/70 transition-colors hover:border-[#3B82F6]/40 hover:text-white"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#93C5FD]">
                Exclusive Partner
              </span>
              <img
                src={mojenaocaleLogo}
                alt="MojeNaočale.ba"
                className="h-5 w-auto object-contain"
              />
            </a>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#features" className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                  Features
                </a>
              </li>
              <li>
                <a href="/#partnership" className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                  Partner Launch
                </a>
              </li>
              <li><span className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors cursor-pointer">Integrations</span></li>
              <li>
                <a
                  href="/api-docs"
                  onClick={handleApiDocsClick}
                  className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors"
                >
                  API Docs
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3">
              <li><span className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors cursor-pointer">About</span></li>
              <li>
                {onNavigateToBlog ? (
                  <a href="/blog" onClick={handleBlogClick} className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                    Blog
                  </a>
                ) : (
                  <a href="/blog" className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">Blog</a>
                )}
              </li>
              <li><span className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors cursor-pointer">Contact</span></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <a href="/privacy" onClick={handlePrivacyClick} className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                  Privacy
                </a>
              </li>
              <li>
                <a href="/terms" onClick={handleTermsClick} className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                  Terms
                </a>
              </li>
              <li>
                <a href="/privacy#cookies" onClick={handleCookiePolicyClick} className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors">
                  Cookie Policy
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('cookie-preferences-requested'))}
                  className="text-sm text-white/50 hover:text-[#3B82F6] transition-colors"
                >
                  Cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/40">© 2026 VisionLive. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <button type="button" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3B82F6] transition-colors text-white/60 hover:text-white" aria-label="Twitter">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
            </button>
            <button type="button" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3B82F6] transition-colors text-white/60 hover:text-white" aria-label="LinkedIn">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </button>
            <button type="button" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#3B82F6] transition-colors text-white/60 hover:text-white" aria-label="Instagram">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
